import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings, UserCog, Home, Loader2, Camera } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export default function AccountMenu() {
  const { user, userRole, signOut } = useAuth();
  const { toast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadProfile = async () => {
    if (!user) return;
    const { data } = await supabase.from('profiles').select('display_name, avatar_url').eq('user_id', user.id).maybeSingle();
    setDisplayName((data as any)?.display_name || '');
    setAvatarUrl((data as any)?.avatar_url || null);
  };

  useEffect(() => { loadProfile(); }, [user?.id]);

  const openDialog = () => {
    setMenuOpen(false);
    setNameInput(displayName);
    setEmailInput(user?.email || '');
    setAvatarFile(null);
    setAvatarPreview(null);
    setDialogOpen(true);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/') || f.size > 5 * 1024 * 1024) {
      toast({ title: 'Ungültiges Bild', description: 'Bitte ein Bild bis 5 MB wählen.', variant: 'destructive' });
      return;
    }
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      let newAvatar = avatarUrl;
      if (avatarFile) {
        const ext = avatarFile.name.split('.').pop() || 'png';
        const path = `avatars/${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('gallery-images').upload(path, avatarFile, { upsert: true });
        if (upErr) throw upErr;
        newAvatar = supabase.storage.from('gallery-images').getPublicUrl(path).data.publicUrl;
      }
      const { error: pErr } = await supabase
        .from('profiles')
        .update({ display_name: nameInput.trim() || null, avatar_url: newAvatar } as any)
        .eq('user_id', user.id);
      if (pErr) throw pErr;

      const email = emailInput.trim();
      let emailMsg = '';
      if (email && email !== user.email) {
        const { error: eErr } = await supabase.auth.updateUser({ email });
        if (eErr) throw eErr;
        emailMsg = ' Bitte bestätige die neue E-Mail-Adresse über den Link in deinem Postfach.';
      }
      setDisplayName(nameInput.trim());
      setAvatarUrl(newAvatar);
      setDialogOpen(false);
      toast({ title: 'Konto gespeichert', description: `Deine Änderungen wurden übernommen.${emailMsg}` });
    } catch (err: any) {
      toast({ title: 'Speichern fehlgeschlagen', description: err?.message || 'Unbekannter Fehler', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try { await signOut(); } finally { window.location.href = '/auth'; }
  };

  if (!user) return null;
  const label = displayName || user.email || '';
  const initial = label.charAt(0).toUpperCase();

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-full pl-1 pr-1 sm:pr-3 h-9 hover:bg-accent transition-colors shrink-0"
            aria-label="Konto-Menü öffnen"
          >
            <Avatar className="h-7 w-7">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={label} className="object-cover" />}
              <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">{initial}</AvatarFallback>
            </Avatar>
            <span className="hidden md:inline max-w-[160px] truncate text-sm">{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-0">
          <div className="flex items-center gap-3 p-4 border-b">
            <Avatar className="h-10 w-10">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={label} className="object-cover" />}
              <AvatarFallback className="bg-primary/20 text-primary font-semibold">{initial}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              {displayName && <p className="text-sm font-medium truncate">{displayName}</p>}
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              {userRole && <Badge variant="secondary" className="mt-1 text-xs">{userRole}</Badge>}
            </div>
          </div>
          <div className="p-1">
            <Button variant="ghost" className="w-full justify-start gap-2" onClick={openDialog}>
              <UserCog className="h-4 w-4" /> Kontoverwaltung
            </Button>
            {userRole === 'admin' && (
              <Button variant="ghost" className="w-full justify-start gap-2" asChild onClick={() => setMenuOpen(false)}>
                <Link to="/admin/settings"><Settings className="h-4 w-4" /> Einstellungen</Link>
              </Button>
            )}
            <Button variant="ghost" className="w-full justify-start gap-2" asChild onClick={() => setMenuOpen(false)}>
              <Link to="/"><Home className="h-4 w-4" /> Zur Website</Link>
            </Button>
            <div className="my-1 h-px bg-border" />
            <Button variant="ghost" className="w-full justify-start gap-2 text-destructive hover:text-destructive" onClick={handleSignOut}>
              <LogOut className="h-4 w-4" /> Abmelden
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>Kontoverwaltung</DialogTitle>
            <DialogDescription>Name, E-Mail-Adresse und Profilbild ändern.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => fileRef.current?.click()} className="relative group" aria-label="Profilbild ändern">
                <Avatar className="h-16 w-16">
                  {(avatarPreview || avatarUrl) && <AvatarImage src={avatarPreview || avatarUrl || ''} className="object-cover" />}
                  <AvatarFallback className="bg-primary/20 text-primary text-lg font-semibold">{initial}</AvatarFallback>
                </Avatar>
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="h-5 w-5" />
                </span>
              </button>
              <div className="flex flex-col gap-1">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>Bild auswählen</Button>
                {(avatarUrl || avatarPreview) && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setAvatarFile(null); setAvatarPreview(null); setAvatarUrl(null); }}>
                    Bild entfernen
                  </Button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-name">Name</Label>
              <Input id="acc-name" value={nameInput} onChange={(e) => setNameInput(e.target.value)} maxLength={100} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-email">E-Mail-Adresse</Label>
              <Input id="acc-email" type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} maxLength={255} />
              <p className="text-xs text-muted-foreground">Eine neue Adresse muss per Bestätigungslink aktiviert werden.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
